// @flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import truncateUrl from 'app/common/lib/util/truncateUrl';
import { Box, Icon, Text } from 'gestalt';

const DOMAIN_MAX_LENGTH = 23;
const DOMAIN_MAX_LENGTH_DENSE_DISPLAY = 15;

export type LinkTextColor = 'white' | 'darkGray';
export type LinkBackgroundColor = 'lightGray' | 'transparent' | 'transparentDarkGray';
export type LinkBorderRadius = 4 | 20;
export type LinkPosition = 'absolute' | 'relative' | 'static';

export type LinkStyleProps = {
  textColor?: LinkTextColor,
  backgroundColor?: LinkBackgroundColor,
  borderRadius?: LinkBorderRadius,
  hoverStyle?: Object,
  position?: LinkPosition,
  bottom?: boolean,
  maxHeight?: number,
  paddingX?: 0 | 1,
};

type PinRepClickThroughAffordanceProps = LinkStyleProps & {
  denseViewSelected?: boolean,
  domain: string,
  isEligibleForWebCloseup?: ?boolean,
  pinId: string,
  sourceUrl: ?string,
  onClick: () => void,
};

type State = {|
  hovered: boolean,
  focused: boolean,
  active: boolean,
|};

export default class PinRepClickThroughAffordance extends React.Component<
  PinRepClickThroughAffordanceProps,
  State,
> {
  static defaultProps = {
    textColor: 'darkGray',
    backgroundColor: 'lightGray',
    borderRadius: 4,
    position: 'absolute',
    bottom: true,
    hoverStyle: {},
    paddingX: 1,
  };

  state: State = {
    hovered: false,
    focused: false,
    active: false,
  };

  handleFocus = () => {
    this.setState({ focused: true });
  };

  handleMouseDown = () => {
    this.setState({ active: true });
  };

  handleMouseUp = () => {
    this.setState({ active: false });
  };

  handleMouseEnter = () => {
    this.setState({ hovered: true });
  };

  handleMouseLeave = () => {
    this.setState({
      active: false,
      hovered: false,
    });
  };

  renderRightArrowClickthrough = () => {
    const {
      backgroundColor,
      borderRadius,
      denseViewSelected,
      domain,
      textColor,
      maxHeight,
    } = this.props;

    const maxLength = denseViewSelected ? DOMAIN_MAX_LENGTH_DENSE_DISPLAY : DOMAIN_MAX_LENGTH;
    const truncatedDomain = truncateUrl(domain, { maxLength });

    return (
      <Box
        padding={2}
        color={backgroundColor}
        display="flex"
        justifyContent="between"
        alignItems="center"
        dangerouslySetInlineStyle={{ __style: { borderRadius } }}
        maxHeight={maxHeight}
      >
        <Box display="flex" flex="none">
          <Icon
            icon="arrow-up-right"
            accessibilityLabel={i18n._('link', 'Website url link')}
            size={14}
            color={textColor}
          />
        </Box>
        <Box marginLeft={2} height={18} flex="grow" overflow="hidden">
          <Text color={textColor} size="sm" truncate weight="bold">
            {truncatedDomain}
          </Text>
        </Box>
      </Box>
    );
  };

  render() {
    const {
      borderRadius,
      bottom,
      hoverStyle,
      isEligibleForWebCloseup,
      onClick,
      pinId,
      position,
      sourceUrl,
      paddingX,
    } = this.props;

    if (!sourceUrl) {
      return null;
    }

    const activeStyles =
      this.state.hovered || this.state.focused || this.state.active ? hoverStyle : {};

    return (
      <Box position={position} bottom={bottom}>
        <Box
          alignItems="end"
          display="flex"
          justifyContent="between"
          marginLeft={-1}
          marginRight={-1}
          padding={2}
        >
          <Box minWidth={0} paddingX={paddingX}>
            {!isEligibleForWebCloseup && (
              <Box
                position="relative"
                dangerouslySetInlineStyle={{ __style: { borderRadius, ...activeStyles } }}
                onFocus={this.handleFocus}
                onMouseDown={this.handleMouseDown}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                onMouseUp={this.handleMouseUp}
              >
                <NavigateLink
                  external
                  onClick={onClick}
                  pinId={pinId}
                  stopPropagation
                  to={sourceUrl}
                >
                  {this.renderRightArrowClickthrough()}
                </NavigateLink>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
}
